import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { forwardRef } from 'react';
import { CheckmarkIcon } from '@paypalcorp/pp-react-icons';
import { Shimmer } from '@paypalcorp/pp-react-shimmer';
import { LoadingSpinner } from '@paypalcorp/pp-react-loading-spinner';
import { screenReaderStyles } from '@paypalcorp/pp-react-screen-reader';
import { cx } from '@paypalcorp/pp-react-utils';
import * as styles from './styles.css';
export { styles as iconButtonStyles };

var _excluded = ["as", "iconName", "svgIconComponent", "className", "label", "iconClassName", "children", "buttonType", "btnState", "screenReaderText", "inverse", "size", "isLoading"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var IconButton = forwardRef(function (props, ref) {
  var as = props.as,
    iconName = props.iconName,
    svgIconComponent = props.svgIconComponent,
    className = props.className,
    label = props.label,
    iconClassName = props.iconClassName,
    children = props.children,
    _props$buttonType = props.buttonType,
    buttonType = _props$buttonType === void 0 ? 'primary' : _props$buttonType,
    _props$btnState = props.btnState,
    btnState = _props$btnState === void 0 ? 'initial' : _props$btnState,
    screenReaderText = props.screenReaderText,
    inverse = props.inverse,
    _props$size = props.size,
    size = _props$size === void 0 ? 'lg' : _props$size,
    isLoading = props.isLoading,
    rest = _objectWithoutProperties(props, _excluded);
  var containerClasseName = size !== 'sm' && className;
  if (isLoading) {
    var shimmerSizesStyleMap = {
      sm: styles.shimmerSizeSm,
      xl: styles.shimmerSizeXl,
      lg: styles.shimmerSizeLg
    };
    var shimmerClasses = cx(styles.shimmer, shimmerSizesStyleMap[size], containerClasseName);
    var _restProps = size !== 'sm' ? _objectSpread({}, rest) : {};
    var iconShimmer = /*#__PURE__*/React.createElement(Shimmer, _extends({
      className: shimmerClasses,
      isInverse: inverse,
      hasStyleDefaults: false
    }, _restProps, {
      "data-ppui": "true"
    }), children);
    if (size === 'sm') return /*#__PURE__*/React.createElement(as || 'button', _extends({
      className: cx(styles.smallSizeTapContainer, className)
    }, rest, {
      "data-ppui": "true"
    }), iconShimmer);
    return iconShimmer;
  }
  var iconSize = {
    sm: 'xs',
    xl: 'md',
    lg: 'sm'
  }[size];
  var sizesStyleMap = {
    sm: styles.sizeSm,
    xl: styles.sizeXl,
    lg: styles.sizeLg
  };

  // Check if the icon button will be wrapped inside a label container or tab target
  var shouldWrapIcon = label || size === 'sm';
  var iconBtnClasses = cx(styles.base, sizesStyleMap[size], buttonType === 'primary' && styles.primary, buttonType === 'secondary' && styles.secondary, buttonType === 'tertiary' && styles.tertiary, inverse && styles.inverse, buttonType === 'secondary' && inverse && styles.secondaryInverse, buttonType === 'tertiary' && inverse && styles.tertiaryInverse, btnState === 'success' && styles.successState, btnState === 'processing' && styles.processingState, !shouldWrapIcon && containerClasseName);
  var withLabelClasses = cx(styles.base, size === 'sm' ? '' : sizesStyleMap[size], styles.secondary, btnState === 'success' && styles.successState, btnState === 'processing' && styles.processingState);
  var Component = as || 'button';
  if (label || size === 'sm') {
    Component = 'div';
  }

  // Setting type to null will not add the attribute:
  var type = Component === 'button' ? 'button' : null;
  var renderIconComponent = svgIconComponent ? /*#__PURE__*/React.createElement(svgIconComponent, {
    className: cx(styles.icon, iconClassName),
    name: iconName,
    size: iconSize,
    "aria-hidden": "true",
    "data-ppui": "true"
  }) : null;

  // Do not add rest props when there is a label or the size is sm. The `iconButtonComponent` component will be inside a wrapper for these two cases
  var restProps = !shouldWrapIcon ? _objectSpread({}, rest) : {};
  var componentProps = _objectSpread({
    className: label ? withLabelClasses : iconBtnClasses,
    ref: ref,
    type: type,
    disabled: btnState && btnState !== 'initial'
  }, restProps);
  var iconButtonComponent = /*#__PURE__*/React.createElement(Component, _extends({
    "data-ppui-info": "icon-button_4.0.6__8.5.0"
  }, componentProps), btnState !== 'initial' ? function renderButtonState() {
    var buttonStateComponent = /*#__PURE__*/React.createElement(CheckmarkIcon, {
      size: iconSize,
      className: styles.icon,
      "data-ppui": "true"
    });
    if (btnState === 'processing') {
      var loadingSpinnerProps = {
        className: cx(styles.icon, styles.loadingSpinner),
        inverse: label ? false : buttonType === 'primary' || buttonType === 'tertiary' && inverse,
        size: iconSize
      };
      buttonStateComponent = /*#__PURE__*/React.createElement(LoadingSpinner, _extends({}, loadingSpinnerProps, {
        "data-ppui": "true"
      }));
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, buttonStateComponent, /*#__PURE__*/React.createElement("span", {
      className: screenReaderStyles.screenReader,
      role: "status",
      "data-ppui": "true"
    }, screenReaderText || btnState));
  }() : /*#__PURE__*/React.createElement(React.Fragment, null, renderIconComponent, !label && /*#__PURE__*/React.createElement("span", {
    className: screenReaderStyles.screenReader,
    "data-ppui": "true"
  }, children)));
  if (!label) {
    if (size === 'sm') {
      return /*#__PURE__*/React.createElement(as || 'button', _extends({
        className: cx(styles.smallSizeTapContainer, className)
      }, rest, {
        "data-ppui": "true"
      }), iconButtonComponent);
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, iconButtonComponent);
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, function iconButtonComponentWithLabel() {
    var WrapperComponent = as !== null && as !== void 0 ? as : 'button';
    var containerClasses = cx(styles.container, btnState === 'success' && styles.successContainer, btnState === 'processing' && styles.processingContainer, className);
    return /*#__PURE__*/React.createElement(WrapperComponent, _extends({
      className: containerClasses,
      tabIndex: 0
    }, rest, {
      "data-ppui": "true"
    }), iconButtonComponent, /*#__PURE__*/React.createElement("span", {
      className: styles.label,
      "data-ppui": "true"
    }, label));
  }());
});

export { IconButton };
